import type { UiMode } from "@forestsync/db";

declare global {
  interface Window {
    gtag?: (...args: [string, ...unknown[]]) => void;
    clarity?: (...args: [string, ...unknown[]]) => void;
  }
}

// ── Event taxonomy (discriminated union) ──

type AnalyticsEventMap = {
  mode_toggle: { from_mode: UiMode; to_mode: UiMode };
  order_placed: {
    mode: UiMode;
    side: string;
    credit_type: string;
    volume: number;
    order_type?: string;
    price?: number;
  };
  chart_interaction: { chart_type: string; action: "range_change" };
  project_viewed: { credit_type: string; vintage: number; mode: UiMode };
  market_viewed: { credit_type?: string; vintage?: number };
  cta_click: { cta_location: string; cta_text: string };
  sign_up: { method: string };
  // Stubs for future epics
  mandate_created: { criteria_count: number };
  forward_requested: { delivery_date: string; volume: number };
};

export type AnalyticsEvent = {
  [K in keyof AnalyticsEventMap]: { name: K } & AnalyticsEventMap[K];
}[keyof AnalyticsEventMap];

export interface CommonContext {
  ui_mode: UiMode;
  user_authenticated: boolean;
  page_path: string;
}

// ── Path sanitization ──

const ROUTE_PATTERNS: [RegExp, string][] = [
  [/^\/market\/([^/]+)\/(\d{4})$/, "/market/:creditType/:vintage"],
  [/^\/trade-orders\/([^/]+)$/, "/trade-orders/:id"],
  [/^\/orders\/([^/]+)$/, "/orders/:id"],
];

export function sanitizePath(path: string): string {
  for (const [pattern, replacement] of ROUTE_PATTERNS) {
    if (pattern.test(path)) return replacement;
  }
  // Fallback: inline regexes to avoid stateful global-flag issues with module-level constants
  return path
    .replace(/\/[0-9a-f-]{8,}/gi, "/:id")
    .replace(/\/\d+/g, "/:id");
}

// ── Kill-switch check ──

function isEnabled(): boolean {
  return (
    typeof window !== "undefined" &&
    process.env.NEXT_PUBLIC_ANALYTICS_ENABLED !== "false"
  );
}

// ── Core functions ──

export function trackEvent(event: AnalyticsEvent, context: CommonContext): void {
  if (!isEnabled()) return;

  const { name, ...params } = event;

  // GA4: structured params
  if (typeof window.gtag === "function") {
    window.gtag("event", name, { ...params, ...context });
  }

  // Clarity: event tag + set key-value metadata
  if (typeof window.clarity === "function") {
    window.clarity("event", name);
    // Attach key context as session-level custom tags
    window.clarity("set", "ui_mode", context.ui_mode);
    window.clarity("set", "user_authenticated", String(context.user_authenticated));
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        window.clarity("set", key, String(value));
      }
    }
  }
}

export function identifyUser(userId: string): void {
  if (!isEnabled()) return;

  if (typeof window.gtag === "function") {
    window.gtag("set", { user_id: userId });
  }
  if (typeof window.clarity === "function") {
    window.clarity("identify", userId);
  }
}

/** Clears analytics identity locally without broadcasting to other tabs */
export function clearIdentityLocal(): void {
  if (!isEnabled()) return;

  if (typeof window.gtag === "function") {
    window.gtag("set", { user_id: null });
  }
  // Note: Clarity has no documented "clear identity" API — don't pass null to identify()
}

/** Clears analytics identity locally and broadcasts to other tabs */
export function clearIdentity(): void {
  clearIdentityLocal();

  if (!isEnabled()) return;
  if (typeof BroadcastChannel !== "undefined") {
    const bc = new BroadcastChannel("analytics-identity-clear");
    bc.postMessage("clear");
    bc.close();
  }
}
